import { useMemo } from 'react';
import { TextField, TextArea, Label, FieldError } from '@heroui/react';
import {
  FieldValues,
  Path,
  useController,
  UseControllerProps
} from 'react-hook-form';
import { useGlobalComponent } from '@rhino-project/core/hooks';
import { stripRhinoProps } from './utils/stripRhinoProps';

export type FieldTextareaProps<T extends FieldValues = FieldValues> = Omit<
  UseControllerProps<T>,
  'name' | 'control'
> & {
  accessor?: (value: unknown) => string | null | undefined;
  path: Path<T>;
  label?: string;
  placeholder?: string;
  isRequired?: boolean;
  isClearable?: boolean;
  onClear?: () => void;
  className?: string;
  [key: string]: unknown;
};

export const FieldTextareaBase = <T extends FieldValues = FieldValues>({
  accessor,
  label,
  placeholder,
  isRequired,
  className,
  ...props
}: FieldTextareaProps<T>) => {
  const { path } = props;
  const {
    field: { disabled, value: fieldValue, ...fieldProps },
    fieldState: { error }
  } = useController({
    name: path
  });

  const value = useMemo(
    () => (accessor ? accessor(fieldValue) : fieldValue),
    [accessor, fieldValue]
  );

  return (
    <TextField
      {...fieldProps}
      isInvalid={!!error}
      isDisabled={disabled}
      isRequired={isRequired}
      value={value || ''}
      className={className}
      {...stripRhinoProps(props)}
    >
      {label && <Label>{label}</Label>}
      <TextArea autoComplete="off" placeholder={placeholder} />
      <FieldError>{error?.message}</FieldError>
    </TextField>
  );
};

export const FieldTextarea = (props: FieldTextareaProps) =>
  useGlobalComponent('FieldTextarea', FieldTextareaBase, props);
